import React, { useState, useEffect, useMemo } from 'react';
import { Scale, RefreshCw } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { toast } from 'sonner';
import { useT } from '../context/LanguageContext';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
const debetNatuur = (rubriek) => rubriek === 'Activa' || rubriek === 'Kosten';

export default function Balans() {
  const { t } = useT();
  const [rekeningen, setRekeningen] = useState([]);
  const [posten, setPosten] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([carsysApi.getGrootboekRekeningen(), carsysApi.getJournaalposten()])
    .then(([r, j]) => { setRekeningen(r); setPosten(j); })
    .catch(() => {});

  useEffect(() => { load(); }, []);

  const saldi = useMemo(() => {
    const m = {};
    rekeningen.forEach(r => { m[r.code] = { ...r, debet: 0, credit: 0 }; });
    posten.forEach(j => (j.regels || []).forEach(l => {
      if (!m[l.rekening]) m[l.rekening] = { code: l.rekening, naam: l.rekening, rubriek: 'Overig', debet: 0, credit: 0 };
      m[l.rekening].debet += (Number(l.debet) || 0);
      m[l.rekening].credit += (Number(l.credit) || 0);
    }));
    Object.values(m).forEach(a => { a.saldo = debetNatuur(a.rubriek) ? a.debet - a.credit : a.credit - a.debet; });
    return Object.values(m);
  }, [rekeningen, posten]);

  const activa = saldi.filter(a => a.rubriek === 'Activa' && Math.abs(a.saldo) > 0.005).sort((a, b) => a.code.localeCompare(b.code));
  const passiva = saldi.filter(a => a.rubriek === 'Passiva' && Math.abs(a.saldo) > 0.005).sort((a, b) => a.code.localeCompare(b.code));
  const opbrengsten = saldi.filter(a => a.rubriek === 'Opbrengsten').reduce((s, a) => s + a.saldo, 0);
  const kosten = saldi.filter(a => a.rubriek === 'Kosten').reduce((s, a) => s + a.saldo, 0);
  const resultaat = opbrengsten - kosten;

  const totaalActiva = activa.reduce((s, a) => s + a.saldo, 0);
  const totaalPassiva = passiva.reduce((s, a) => s + a.saldo, 0) + resultaat;
  const inBalans = Math.abs(totaalActiva - totaalPassiva) < 0.02;

  const genereer = async () => {
    setBusy(true);
    try { const res = await carsysApi.genereerBoekhouding(); toast.success(res.created > 0 ? `${res.created} ${t('posten bijgewerkt', 'posten bijgewerkt')}` : t('Al up-to-date', 'Al up-to-date')); await load(); }
    catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="balans-page">
      <div className="cs-page-header">
        <div><h2>{t('Balans', 'Balans')}</h2><p>{t('Activa en passiva op basis van je grootboek. Activa = Passiva + Resultaat.', 'Activa en passiva op basis van je grootboek. Activa = Passiva + Resultaat.')}</p></div>
        <button className="cs-btn primary" data-testid="btn-balans-genereer" disabled={busy} onClick={genereer}><RefreshCw size={14} /> {busy ? t('Bezig...', 'Bezig...') : t('Bijwerken', 'Bijwerken')}</button>
      </div>

      <div style={{ marginBottom: 12 }}>
        <span className={`cs-badge ${inBalans ? 'green' : 'red'}`} data-testid="balans-status">
          <Scale size={12} style={{ verticalAlign: '-1px' }} /> {inBalans ? t('In balans', 'In balans') : t('Niet in balans — werk de boekhouding bij', 'Niet in balans — werk de boekhouding bij')}
        </span>
      </div>

      <div className="cs-two-col">
        {/* ACTIVA */}
        <div className="cs-card">
          <h3 className="cs-card-title" style={{ padding: '12px 14px 0' }}>{t('Activa (bezittingen)', 'Activa (bezittingen)')}</h3>
          <table className="cs-table" data-testid="balans-activa">
            <tbody>
              {activa.map(a => (
                <tr key={a.code}><td style={{ fontFamily: 'ui-monospace, monospace', width: 60 }}>{a.code}</td><td>{a.naam}</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{eur(a.saldo)}</td></tr>
              ))}
              {activa.length === 0 && <tr><td colSpan={3} className="cs-empty">{t('Geen activa geboekt', 'Geen activa geboekt')}</td></tr>}
            </tbody>
            <tfoot><tr style={{ fontWeight: 700, borderTop: '2px solid var(--cs-border)' }}><td colSpan={2}>{t('Totaal activa', 'Totaal activa')}</td><td style={{ textAlign: 'right' }} data-testid="balans-totaal-activa">{eur(totaalActiva)}</td></tr></tfoot>
          </table>
        </div>

        {/* PASSIVA */}
        <div className="cs-card">
          <h3 className="cs-card-title" style={{ padding: '12px 14px 0' }}>{t('Passiva (schulden & vermogen)', 'Passiva (schulden & vermogen)')}</h3>
          <table className="cs-table" data-testid="balans-passiva">
            <tbody>
              {passiva.map(a => (
                <tr key={a.code}><td style={{ fontFamily: 'ui-monospace, monospace', width: 60 }}>{a.code}</td><td>{a.naam}</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{eur(a.saldo)}</td></tr>
              ))}
              <tr data-testid="balans-resultaat"><td style={{ fontFamily: 'ui-monospace, monospace', width: 60 }}>—</td><td>{t('Resultaat boekjaar', 'Resultaat boekjaar')} ({resultaat >= 0 ? t('winst', 'winst') : t('verlies', 'verlies')})</td><td style={{ textAlign: 'right', fontWeight: 600, color: resultaat >= 0 ? 'var(--cs-green)' : 'var(--cs-red)' }}>{eur(resultaat)}</td></tr>
            </tbody>
            <tfoot><tr style={{ fontWeight: 700, borderTop: '2px solid var(--cs-border)' }}><td colSpan={2}>{t('Totaal passiva', 'Totaal passiva')}</td><td style={{ textAlign: 'right' }} data-testid="balans-totaal-passiva">{eur(totaalPassiva)}</td></tr></tfoot>
          </table>
        </div>
      </div>

      <p style={{ fontSize: 11, color: 'var(--cs-text-muted)', marginTop: 12 }}>
        {t('Indicatieve balans op basis van automatisch gegenereerde journaalposten. Voor de officiële jaarrekening: raadpleeg je boekhouder.', 'Indicatieve balans op basis van automatisch gegenereerde journaalposten. Voor de officiële jaarrekening: raadpleeg je boekhouder.')}
      </p>
    </div>
  );
}
